import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import EscalationConsentModal from './EscalationConsentModal';

/**
 * "Talk to a real person about this" — shown on every advice surface.
 * Compiles pre-briefed packet, matches verified CFP, archives immutably.
 */
const TalkToAdvisorButton = ({
  userQuestion,
  surface = 'chat',
  framing = '',
  reasoning = '',
  advicePayload = null,
  escalationPacketId = null,
  primaryAdvisorId = null,
  className = '',
  compact = false,
}) => {
  const navigate = useNavigate();
  const [showConsent, setShowConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const question =
    userQuestion ||
    advicePayload?.verdict ||
    'I would like to discuss my financial situation with an advisor.';

  const handleEscalate = async (consented) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        '/api/advice/escalate',
        {
          message: question,
          surface,
          framing: framing || advicePayload?.verdict || '',
          reasoning: reasoning || framing || '',
          advice_payload: advicePayload,
          unified_brain_consented: consented,
        },
        { timeout: 20000 },
      );
      const data = res.data || {};
      const packetId = data.packet_id || escalationPacketId;
      const advisorId = data.primary_advisor_id || primaryAdvisorId;
      setShowConsent(false);
      if (advisorId) {
        navigate(`/advisor-network/${advisorId}?packet=${packetId || ''}`);
      } else {
        navigate(`/advisor-network${packetId ? `?packet=${packetId}` : ''}`);
      }
    } catch (err) {
      console.error('Escalation failed:', err);
      setError('Could not prepare your advisor briefing. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setShowConsent(true)}
        disabled={loading}
        className={
          className ||
          (compact
            ? 'inline-flex items-center gap-1.5 text-label text-pine hover:text-primary transition-colors disabled:opacity-50'
            : 'inline-flex items-center gap-2 mt-3 text-body text-pine hover:text-primary border border-pine/30 hover:border-pine/60 rounded-md px-3 py-2 transition-colors disabled:opacity-50')
        }
      >
        <UserGroupIcon className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        Talk to a real person about this
      </button>
      {error && <p className="text-label text-danger mt-1">{error}</p>}

      {showConsent && (
        <EscalationConsentModal
          onShare={() => handleEscalate(true)}
          onDecline={() => handleEscalate(false)}
          onClose={() => setShowConsent(false)}
          loading={loading}
        />
      )}
    </>
  );
};

export default TalkToAdvisorButton;
